import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { X, UserPlus, UserMinus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FollowListModal = ({ isOpen, onClose, userId, type, title }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (isOpen && userId) {
      loadUsers();
    }
  }, [isOpen, userId, type]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const endpoint = type === 'followers' 
        ? `${API}/users/${userId}/followers`
        : `${API}/users/${userId}/following`;
      
      const response = await axios.get(endpoint, { withCredentials: true });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (targetUserId) => {
    setActionLoading(prev => ({ ...prev, [targetUserId]: true }));
    try {
      const user = users.find(u => u.user_id === targetUserId);
      if (user?.is_following) {
        await axios.delete(`${API}/users/${targetUserId}/follow`, { withCredentials: true });
      } else {
        await axios.post(`${API}/users/${targetUserId}/follow`, {}, { withCredentials: true });
      }
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === targetUserId 
          ? { ...u, is_following: !u.is_following }
          : u
      ));
    } catch (error) {
      console.error('Error following/unfollowing:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  const handleUserClick = (targetUserId) => {
    onClose();
    navigate(`/app/profile/${targetUserId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            {title} ({total})
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {type === 'followers' 
                ? 'Aucun abonné pour le moment'
                : 'Ne suit personne pour le moment'
              }
            </div>
          ) : (
            <div className="space-y-1">
              {users.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => handleUserClick(user.user_id)}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-semibold">
                        {user.display_name?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {user.display_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {user.bio?.substring(0, 50) || 'Aucune bio'}
                        {user.bio?.length > 50 ? '...' : ''}
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    size="sm"
                    variant={user.is_following ? "outline" : "default"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollow(user.user_id);
                    }}
                    disabled={actionLoading[user.user_id]}
                    className={!user.is_following ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white" : ""}
                  >
                    {actionLoading[user.user_id] ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                    ) : user.is_following ? (
                      <>
                        <UserMinus className="h-4 w-4 mr-1" />
                        Suivi
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-1" />
                        Suivre
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowListModal;
